import { useState, useEffect, useMemo } from "react";
import { balanceService } from "../services/api";
import {
  formatCurrency,
  toPersianNumbers,
  formatJalaliDate,
} from "../utils/numbers";
import { Wallet, ChevronLeft, ChevronRight } from "lucide-react";

interface Transaction {
  id: number;
  type:
    | "investment"
    | "loan_fee"
    | "installment"
    | "penalty"
    | "expense"
    | "paid_loan";
  first_name: string;
  last_name: string;
  amount: string | number;
  date: string;
  description?: string;
}

const TABS = [
  { id: "all", label: "همه موارد" },
  { id: "investment", label: "سرمایه‌گذاری‌ها" },
  { id: "loan_fee", label: "کارمزدهای وام" },
  { id: "penalty", label: "جریمه‌های دیرکرد" },
  { id: "installment", label: "اقساط پرداخت شده" },
  { id: "expense", label: "هزینه‌ها" },
  { id: "paid_loan", label: "وام‌های پرداختی" },
];

const TYPE_LABELS: Record<string, string> = {
  investment: "سرمایه‌گذاری",
  loan_fee: "کارمزد ثبت وام",
  installment: "قسط پرداخت شده",
  penalty: "جریمه دیرکرد",
  expense: "هزینه",
  paid_loan: "پرداخت وام",
};

const TYPE_COLORS: Record<string, string> = {
  investment:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  loan_fee: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  installment:
    "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  penalty:
    "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  expense: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  paid_loan:
    "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400",
};

export default function Balance() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState({ currentBalance: 0, futureValue: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("all");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [data, summaryData] = await Promise.all([
        balanceService.getTransactions(),
        balanceService.getSummary(),
      ]);
      setTransactions(data);
      setSummary(summaryData);
    } catch (error) {
      console.error("Error fetching balance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalBalance = summary.currentBalance;
  const futureValue = summary.futureValue;

  const filteredTransactions = useMemo(() => {
    let filtered = transactions;
    if (activeTab !== "all") {
      filtered = transactions.filter((t) => t.type === activeTab);
    }
    // "همه موارد" will just return all transactions
    return filtered;
  }, [transactions, activeTab]);

  // Reset page when tab or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, itemsPerPage]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-persian-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
            موجودی صندوق
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            گزارش موجودی و تمامی تراکنش‌های مالی صندوق
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-persian-600 to-persian-800 rounded-2xl p-6 shadow-lg shadow-persian-600/20 text-white flex flex-col justify-center items-center text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 p-6 opacity-10">
            <Wallet size={120} />
          </div>
          <p className="text-sm font-medium text-white/80 mb-2 relative z-10">
            موجودی ریالی فعلی صندوق
          </p>
          <p
            className="text-3xl sm:text-4xl font-bold relative z-10 flex items-center justify-center gap-2"
            dir="rtl"
          >
            <span dir="ltr" className="font-bold">
              {totalBalance < 0 ? "-" : ""}
              {toPersianNumbers(Math.abs(totalBalance).toLocaleString())}
            </span>
            <span className="text-lg font-normal opacity-80">ریال</span>
          </p>
        </div>

        <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-6 shadow-lg shadow-emerald-600/20 text-white flex flex-col justify-center items-center text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 p-6 opacity-10">
            <Wallet size={120} />
          </div>
          <p className="text-sm font-medium text-white/80 mb-2 relative z-10">
            ارزش آینده صندوق
          </p>
          <p
            className="text-3xl sm:text-4xl font-bold relative z-10 flex items-center justify-center gap-2"
            dir="rtl"
          >
            <span dir="ltr" className="font-bold">
              {futureValue < 0 ? "-" : ""}
              {toPersianNumbers(Math.abs(futureValue).toLocaleString())}
            </span>
            <span className="text-lg font-normal opacity-80">ریال</span>
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden flex flex-col">
        {/* Tabs */}
        <div className="overflow-x-auto custom-scrollbar border-b border-slate-100 dark:border-slate-700">
          <div className="flex p-2 min-w-max">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-right">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  ردیف
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  نوع تراکنش
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  نام و نام خانوادگی / بابت
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  مبلغ (ریال)
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  تاریخ
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
              {paginatedTransactions.length > 0 ? (
                paginatedTransactions.map((t, index) => (
                  <tr
                    key={`${t.type}-${t.id}`}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {toPersianNumbers(
                        (currentPage - 1) * itemsPerPage + index + 1,
                      )}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-bold ${TYPE_COLORS[t.type] || TYPE_COLORS.paid_loan}`}
                      >
                        {TYPE_LABELS[t.type] || "نامشخص"}
                      </span>
                    </td>
                    <td className="p-4 text-slate-700 dark:text-slate-300">
                      <span className="font-semibold text-slate-900 dark:text-white">
                        {t.first_name} {t.last_name}
                      </span>
                      {t.description && (
                        <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                          {t.description}
                        </span>
                      )}
                    </td>
                    <td
                      className={`p-4 font-bold ${["expense", "paid_loan"].includes(t.type) ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                    >
                      <span className="font-bold ml-1">
                        {["expense", "paid_loan"].includes(t.type) ? "-" : "+"}
                      </span>
                      {formatCurrency(t.amount)}
                    </td>
                    <td className="p-4" dir="ltr">
                      {formatJalaliDate(t.date)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    موردی یافت نشد.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex flex-col md:flex-row justify-between items-center gap-4 bg-slate-50/30 dark:bg-slate-900/20">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                تعداد در صفحه:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-3 py-1.5 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-persian-500 outline-none dark:text-white"
              >
                <option value={10}>{toPersianNumbers(10)}</option>
                <option value={20}>{toPersianNumbers(20)}</option>
                <option value={50}>{toPersianNumbers(50)}</option>
                <option value={100}>{toPersianNumbers(100)}</option>
                <option value={150}>{toPersianNumbers(150)}</option>
                <option value={200}>{toPersianNumbers(200)}</option>
              </select>
            </div>

            <div className="text-sm text-slate-500 dark:text-slate-400">
              نمایش{" "}
              {toPersianNumbers(
                filteredTransactions.length > 0
                  ? (currentPage - 1) * itemsPerPage + 1
                  : 0,
              )}{" "}
              تا{" "}
              {toPersianNumbers(
                Math.min(
                  currentPage * itemsPerPage,
                  filteredTransactions.length,
                ),
              )}{" "}
              از {toPersianNumbers(filteredTransactions.length)} مورد
            </div>
          </div>

          {totalPages > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={18} />
              </button>
              <div className="flex items-center gap-1">
                {(() => {
                  const getVisiblePages = (current: number, total: number) => {
                    if (total <= 5)
                      return Array.from({ length: total }, (_, i) => i + 1);
                    if (current <= 3) return [1, 2, 3, 4, "...", total];
                    if (current >= total - 2)
                      return [1, "...", total - 3, total - 2, total - 1, total];
                    return [
                      1,
                      "...",
                      current - 1,
                      current,
                      current + 1,
                      "...",
                      total,
                    ];
                  };

                  return getVisiblePages(currentPage, totalPages).map(
                    (page, i) =>
                      page === "..." ? (
                        <span key={`dots-${i}`} className="px-1 text-slate-400">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page as number)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? "bg-persian-600 text-white"
                              : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
                          }`}
                        >
                          {toPersianNumbers(page)}
                        </button>
                      ),
                  );
                })()}
              </div>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
